import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronIcon, EmptyState, ErrorState, ExternalIcon, LoadingState, SearchIcon } from "@signet/ui";
import { CreatorAvatar, useCreatorPhotos, useMe, type CreatorPhotos } from "@signet/web-kit";
import { describeTarget, hoursText, shortTarget } from "../shared/targets.ts";
import { addDays, dayLabel, dayLetter, duration, melbourneDay, melbourneTime, weekDays, weekLabel } from "../shared/week.ts";
import { BASE, cbKeys, count, fetchHistory, fetchWeek, roomUrl, type DayCell, type Status, type Week, type WeekRow } from "./api.ts";
import { CbPage } from "./frame.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   CB Stats' Reporting tab (Jay, 2026-11-05): one week, Monday to Sunday,
   every creator's Chaturbate streams against their target (set on Creator
   Setup, with the days they're scheduled; a scheduled day without a stream
   shows as missed). Grouped as
   the weekly report was (didn't stream, didn't meet it, met it), slackers on
   top and most hours streamed first in each group, with a box per day so who
   is slacking reads at a glance, and a search for one creator. A row opens
   to that creator's streams, laid out like the daily update.
   ────────────────────────────────────────────────────────────────────────── */

const GROUPS: { status: Status; past: string; current: string }[] = [
  { status: "none", past: "Didn't stream", current: "Not streamed yet" },
  { status: "behind", past: "Didn't meet the target", current: "Behind" },
  { status: "on", past: "Met the target", current: "On target" },
];

function ago(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `${hours}h ago` : `${Math.round(hours / 24)} days ago`;
}

export function CbStatsPage() {
  const { can } = useMe();
  // Someone who only sets creators up has nothing to see here.
  if (!can("cbstats.stats:view")) return <Navigate to={"/cbstats/setup" as never} replace />;
  return <Reporting />;
}

function Reporting() {
  const { can } = useMe();
  const search = useSearch({ strict: false } as never) as { week?: string };
  const navigate = useNavigate();
  const week = useQuery({ queryKey: cbKeys.week(search.week ?? "this"), queryFn: () => fetchWeek(search.week), refetchInterval: 60_000 });
  const photos = useCreatorPhotos(BASE);
  const data = week.data;
  const [find, setFind] = useState("");
  const words = find.trim().toUpperCase();
  const shown = (data?.rows ?? []).filter((r) => !words || r.creator.includes(words) || r.username.toUpperCase().includes(words));
  const setWeek = (monday: string | null) => navigate({ to: ".", search: (monday ? { week: monday } : {}) as never, replace: true });

  return (
    <CbPage tab="Reporting" intro={<>Who is putting the hours in, against the target set on Creator Setup. Chaturbate is checked every 3 minutes{data?.lastSweep ? `, last ${ago(data.lastSweep)}` : ""}.</>}>

      {data ? (
        <div className="cb-week-bar">
          <button type="button" className="icon-button" aria-label="Week before" onClick={() => setWeek(addDays(data.week, -7))}>
            <ChevronIcon size={15} direction="left" />
          </button>
          <strong>{weekLabel(data.week)}</strong>
          <button type="button" className="icon-button" aria-label="Week after" disabled={data.current} onClick={() => setWeek(addDays(data.week, 7))}>
            <ChevronIcon size={15} direction="right" />
          </button>
          {data.current ? (
            <span className="cb-faint">This week, {data.daysLeft} {data.daysLeft === 1 ? "day" : "days"} left</span>
          ) : (
            <button type="button" className="text-button tiny accent" onClick={() => setWeek(null)}>
              This week
            </button>
          )}
          <div className="cb-search search-box">
            <SearchIcon size={14} />
            <input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Search creators" aria-label="Search creators" autoComplete="off" spellCheck={false} />
          </div>
        </div>
      ) : null}

      {week.isLoading ? (
        <LoadingState />
      ) : week.isError || !data ? (
        <ErrorState title="Couldn't load CB Stats" onRetry={() => week.refetch()} />
      ) : !data.rows.length ? (
        <section className="panel">
          <EmptyState title="No Chaturbate usernames yet" body={can("cbstats.account:create") ? "Add a creator's username on Creator Setup and their streams are tracked from then on." : "Someone with access adds creators' usernames on Creator Setup, and their streams show here."} />
        </section>
      ) : !shown.length ? (
        <section className="panel">
          <p className="cb-quiet">No creator matches that.</p>
        </section>
      ) : (
        GROUPS.map((group) => {
          const rows = shown.filter((r) => r.status === group.status);
          if (!rows.length) return null;
          return <Group key={group.status} title={data.current ? group.current : group.past} status={group.status} rows={rows} week={data} photos={photos} />;
        })
      )}
    </CbPage>
  );
}

function Group({ title, status, rows, week, photos }: { title: string; status: Status; rows: WeekRow[]; week: Week; photos: CreatorPhotos }) {
  const [open, setOpen] = useState<string | null>(null);
  const days = weekDays(week.week);
  return (
    <section className={`panel cb-group cb-${status}`}>
      <div className="panel-head">
        <h2>
          <span className="cb-group-dot" /> {title} <span className="cb-faint">{rows.length}</span>
        </h2>
      </div>
      <div className="panel-body flush">
        <div className="table cb-table">
          <div className="table-row head cb-row">
            <span>Creator</span>
            <span className="cb-days">
              {days.map((d) => (
                <span key={d} className={d === week.today ? "cb-today" : undefined}>{dayLetter(d).slice(0, 2)}</span>
              ))}
            </span>
            <span className="cb-num">Target</span>
            <span className="cb-num">Time live</span>
            <span className="cb-num">Most views</span>
            <span className="cb-num">Avg views</span>
            <span className="cb-num">Last week</span>
          </div>
          {rows.map((row) => (
            <Fragment key={row.account}>
              <button type="button" className={`table-row cb-row cb-click${open === row.account ? " open" : ""}`} aria-expanded={open === row.account} onClick={() => setOpen(open === row.account ? null : row.account)}>
                <span className="cb-who">
                  <CreatorAvatar name={row.creator} src={photos(row.creator)} size={30} />
                  <span className="cb-who-text">
                    <strong>
                      {row.creator}
                      {row.live ? <span className="cb-live" title="Live now">Live</span> : null}
                    </strong>
                    <small>{row.username}</small>
                  </span>
                </span>
                <span className="cb-days">
                  {row.days.map((d, i) => (
                    <DayBox key={days[i]} day={days[i]!} cell={d} week={week} trackedFrom={row.trackedFrom} scheduled={row.schedule.includes(i)} />
                  ))}
                </span>
                <TargetCell row={row} />
                <span className="cb-num">
                  {duration(row.minutes)}
                  <small>{row.streams} {row.streams === 1 ? "stream" : "streams"}</small>
                </span>
                <span className="cb-num">{row.peak === null ? "" : count(row.peak)}</span>
                <span className="cb-num">{row.avg === null ? "" : count(row.avg)}</span>
                <span className="cb-num">
                  {row.lastWeek.tracked ? (
                    <>
                      <span className={tone(row.lastWeek.status)}>{row.lastWeek.done}/{row.lastWeek.need}</span>
                      <small>{duration(row.lastWeek.minutes)}</small>
                    </>
                  ) : (
                    <small>Not tracked</small>
                  )}
                </span>
              </button>
              {open === row.account ? <Detail row={row} /> : null}
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

const tone = (status: Status) => (status === "on" ? "cb-good" : status === "behind" ? "cb-warn" : "cb-bad");

/** Streams that counted of those needed, over the target's own period, with what is still needed while it runs. */
function TargetCell({ row }: { row: WeekRow }) {
  const p = row.progress;
  return (
    <span className="cb-num" title={describeTarget(row.target)}>
      <strong className={tone(p.status)}>
        {p.done}/{p.need}
      </strong>
      <small>
        {p.label}
        {row.own ? <span className="cb-own"> {shortTarget(row.target)}</span> : null}
      </small>
      {p.time ? (
        <small className={p.time.minutes >= p.time.need ? "cb-good" : undefined}>
          {duration(p.time.minutes)} of {hoursText(p.time.need / 60)}
        </small>
      ) : null}
      {p.needs ? <small className={p.late ? "cb-bad" : undefined}>{p.needs}</small> : null}
    </span>
  );
}

/**
 * A day of the week: green when a stream that day counted toward the target, amber when none did,
 * grey for no stream, red when the day was scheduled and passed without one. A scheduled day still to
 * come is outlined, so the week's plan shows too.
 */
function DayBox({ day, cell, week, trackedFrom, scheduled }: { day: string; cell: DayCell | null; week: Week; trackedFrom: string | null; scheduled: boolean }) {
  const label = `${dayLabel(day)}${scheduled ? " (scheduled)" : ""}`;
  const plan = scheduled ? " planned" : "";
  if (day > week.today) return <span className={`cb-day future${plan}`} title={`${label}: still to come`} />;
  if (trackedFrom && day < trackedFrom) return <span className="cb-day untracked" title={`${label}: not tracked yet`} />;
  if (!cell) {
    // Today isn't missed until it's over.
    if (scheduled && day < week.today) return <span className="cb-day missed" title={`${label}: scheduled, didn't stream`}>Missed</span>;
    return <span className={`cb-day off${plan}`} title={`${label}: ${day === week.today ? "nothing yet" : "didn't stream"}`} />;
  }
  const streams = cell.streams > 1 ? ` in ${cell.streams} streams` : "";
  return (
    <span className={`cb-day ${cell.counted ? "met" : "short"}${plan}`} title={`${label}: ${duration(cell.minutes)}${streams}, most views ${count(cell.peak)}${cell.counted ? "" : ", didn't count toward the target"}`}>
      {Math.floor(cell.minutes / 60)}:{String(cell.minutes % 60).padStart(2, "0")}
    </span>
  );
}

function Detail({ row }: { row: WeekRow }) {
  const history = useQuery({ queryKey: cbKeys.history(row.account), queryFn: () => fetchHistory(row.account) });
  const data = history.data;
  return (
    <div className="cb-detail">
      {history.isLoading ? (
        <LoadingState />
      ) : history.isError || !data ? (
        <ErrorState title="Couldn't load the streams" onRetry={() => history.refetch()} />
      ) : (
        <>
          <div className="cb-weeks">
            {data.weeks.map((w) =>
              w.tracked ? (
                <span key={w.week} className={`cb-week-chip ${w.status === "on" ? "met" : w.status === "behind" ? "short" : "off"}`} title={`${weekLabel(w.week)}: ${w.done}/${w.need} ${w.label}, ${duration(w.minutes)} live`}>
                  <small>{weekLabel(w.week).split(" to ")[0]}</small>
                  <strong>{w.done}/{w.need}</strong>
                </span>
              ) : (
                <span key={w.week} className="cb-week-chip untracked" title={`${weekLabel(w.week)}: not tracked yet`}>
                  <small>{weekLabel(w.week).split(" to ")[0]}</small>
                  <strong>-</strong>
                </span>
              ),
            )}
            <span className="cb-faint cb-detail-target">{describeTarget(data.target)}</span>
            <a className="text-button tiny cb-room" href={roomUrl(row.username)} target="_blank" rel="noreferrer">
              chaturbate.com/{row.username} <ExternalIcon size={11} />
            </a>
          </div>
          {data.streams.length ? (
            <div className="table cb-streams">
              <div className="table-row head cb-stream-row">
                <span>Day</span>
                <span>Live</span>
                <span className="cb-num">Duration</span>
                <span className="cb-num">Most views</span>
                <span className="cb-num">Avg views</span>
                <span className="cb-num">New followers</span>
              </div>
              {data.streams.map((s) => (
                <div key={s.start} className="table-row cb-stream-row">
                  <span>{dayLabel(s.day)}</span>
                  <span className="cb-faint">{melbourneTime(s.start)} to {melbourneTime(s.end)}</span>
                  <span className={`cb-num ${s.counted ? "cb-good" : "cb-warn"}`} title={s.counted ? "Counted toward the target" : "Didn't count toward the target"}>{duration(s.minutes)}</span>
                  <span className="cb-num">{count(s.peak)}</span>
                  <span className="cb-num">{count(s.avg)}</span>
                  <span className="cb-num">{s.followersGained ? `+${count(s.followersGained)}` : ""}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="cb-quiet">No streams since {dayLabel(melbourneDay(data.addedAt))}, when tracking started.</p>
          )}
        </>
      )}
    </div>
  );
}
